/**
 * CommercialRequirementsCard Component
 * Displays commercial permitting requirements from jurisdiction data
 *
 * Shows:
 * - Commercial permitting process steps
 * - Required documents (site development plan, architectural, structural, MEP)
 * - Additional agency approvals needed
 * - Department contacts
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Building2,
  FileText,
  Phone,
  Mail,
  ExternalLink,
  ClipboardList,
  AlertTriangle,
  CheckCircle2,
  Landmark,
  HardHat,
} from "lucide-react";
import type { Jurisdiction } from "@/hooks/useCompliance";
import type { CommercialRequirements, PermittingProcessStepDB, AgencyApproval } from "@/types/jurisdiction";

interface CommercialRequirementsCardProps {
  jurisdiction: Jurisdiction;
  showFullProcess?: boolean;
}

/**
 * Get step badge color based on step order
 */
function getStepBadgeVariant(
  order: number
): "default" | "secondary" | "outline" | "destructive" {
  switch (order) {
    case 1:
    case 2:
      return "secondary";
    case 3:
    case 4:
      return "default";
    case 5:
    case 6:
      return "outline";
    case 7:
      return "destructive";
    default:
      return "secondary";
  }
}

export function CommercialRequirementsCard({
  jurisdiction,
  showFullProcess = false,
}: CommercialRequirementsCardProps) {
  const commercialRequirements = jurisdiction.commercialRequirements;
  const permittingProcess = jurisdiction.permittingProcess;
  const departmentContacts = jurisdiction.departmentContacts;

  // Get commercial permitting process if available
  const commercialProcess = permittingProcess?.commercial;

  // If we have no commercial data to display, don't render the card
  if (!commercialRequirements && !commercialProcess) {
    return null;
  }

  const steps = commercialProcess?.steps || [];
  const displaySteps = showFullProcess ? steps : steps.slice(0, 4);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Commercial Permit Requirements
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overview Alert */}
        {commercialProcess?.overview && (
          <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800">
            <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertTitle className="text-blue-800 dark:text-blue-200">
              Commercial Permitting Process
            </AlertTitle>
            <AlertDescription className="text-blue-700 dark:text-blue-300">
              {commercialProcess.overview}
            </AlertDescription>
          </Alert>
        )}

        {/* Permitting Process Steps */}
        {steps.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">Permitting Steps</span>
              </div>
              <Badge variant="secondary">
                {steps.length} steps
              </Badge>
            </div>
            <Accordion type="single" collapsible className="w-full">
              {displaySteps.map((step, idx) => (
                <AccordionItem key={idx} value={`step-${step.order}`} className="border-none">
                  <AccordionTrigger className="py-3 px-3 bg-muted/50 rounded-lg hover:bg-muted/70 mb-1">
                    <span className="flex items-center gap-3 text-sm">
                      <Badge
                        variant={getStepBadgeVariant(step.order)}
                        className="shrink-0"
                      >
                        Step {step.order}
                      </Badge>
                      <span className="font-medium">{step.name}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pt-2">
                    <div className="space-y-3 text-sm">
                      <p className="text-muted-foreground">{step.description}</p>

                      {/* Location & Contact Info */}
                      {(step.location || step.phone || step.email) && (
                        <div className="flex flex-wrap gap-3 text-sm">
                          {step.location && (
                            <span className="text-muted-foreground">
                              <strong>Location:</strong> {step.location}
                            </span>
                          )}
                          {step.phone && (
                            <a
                              href={`tel:${step.phone}`}
                              className="flex items-center gap-1 text-primary hover:underline"
                            >
                              <Phone className="h-3 w-3" />
                              {step.phone}
                            </a>
                          )}
                          {step.email && (
                            <a
                              href={`mailto:${step.email}`}
                              className="flex items-center gap-1 text-primary hover:underline"
                            >
                              <Mail className="h-3 w-3" />
                              {step.email}
                            </a>
                          )}
                        </div>
                      )}

                      {/* Requirements */}
                      {step.requirements && step.requirements.length > 0 && (
                        <div>
                          <p className="font-medium text-xs uppercase tracking-wide text-muted-foreground mb-2">
                            Requirements:
                          </p>
                          <ul className="list-disc ml-4 text-muted-foreground space-y-1">
                            {step.requirements.map((req, reqIdx) => (
                              <li key={reqIdx}>{req}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Reviews performed */}
                      {step.reviews && step.reviews.length > 0 && (
                        <div>
                          <p className="font-medium text-xs uppercase tracking-wide text-muted-foreground mb-2">
                            Reviews:
                          </p>
                          <ul className="list-disc ml-4 text-muted-foreground space-y-1">
                            {step.reviews.map((review, revIdx) => (
                              <li key={revIdx}>{review}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Timeline */}
                      {step.timeline && (
                        <p className="text-sm text-muted-foreground">
                          <strong>Timeline:</strong> {step.timeline}
                        </p>
                      )}

                      {/* Notes */}
                      {step.notes && (
                        <p className="text-sm text-muted-foreground italic">
                          {step.notes}
                        </p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            {!showFullProcess && steps.length > 4 && (
              <p className="text-sm text-muted-foreground text-center py-2">
                + {steps.length - 4} more steps
              </p>
            )}
          </div>
        )}

        {/* Required Documents Section */}
        {commercialRequirements && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">Required Documents</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Site Development Plan */}
              {commercialRequirements.site_development_plan && (
                <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                  <div className="flex items-center gap-2">
                    <Landmark className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">Site Development Plan</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {commercialRequirements.site_development_plan.description}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <strong>Prepared by:</strong> {commercialRequirements.site_development_plan.prepared_by}
                  </p>
                </div>
              )}

              {/* Architectural Drawings */}
              {commercialRequirements.architectural_drawings && (
                <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">Architectural Drawings</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {commercialRequirements.architectural_drawings.description}
                  </p>
                </div>
              )}

              {/* Structural Drawings */}
              {commercialRequirements.structural_drawings && (
                <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                  <div className="flex items-center gap-2">
                    <HardHat className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">Structural Drawings</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {commercialRequirements.structural_drawings.description}
                  </p>
                </div>
              )}

              {/* MEP Drawings */}
              {commercialRequirements.mep_drawings && (
                <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">MEP Drawings</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {commercialRequirements.mep_drawings.description}
                  </p>
                </div>
              )}

              {/* Civil Engineering */}
              {commercialRequirements.civil_engineering_review && (
                <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">Civil Engineering Plans</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {commercialRequirements.civil_engineering_review.description}
                  </p>
                  <Badge variant="outline" className="text-xs">
                    {commercialRequirements.civil_engineering_review.sets_required} sets required
                  </Badge>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Additional Agency Approvals */}
        {commercialRequirements?.additional_agency_approvals &&
         commercialRequirements.additional_agency_approvals.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Landmark className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">Additional Agency Approvals</span>
            </div>
            <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800 mb-3">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <AlertDescription className="text-amber-700 dark:text-amber-300 text-sm">
                Commercial projects may require approvals from multiple external agencies.
                Determine required approvals early as they can add significant time.
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              {commercialRequirements.additional_agency_approvals.slice(0, 5).map((agency, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 p-3 rounded-lg bg-muted/50"
                >
                  <CheckCircle2 className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{agency.agency}</p>
                    <p className="text-xs text-muted-foreground">
                      Required for: {agency.for}
                    </p>
                    {agency.phone && (
                      <a
                        href={`tel:${agency.phone}`}
                        className="text-xs text-primary hover:underline"
                      >
                        {agency.phone}
                      </a>
                    )}
                    {agency.url && (
                      <a
                        href={agency.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                      >
                        Website <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
              {commercialRequirements.additional_agency_approvals.length > 5 && (
                <p className="text-sm text-muted-foreground text-center py-2">
                  + {commercialRequirements.additional_agency_approvals.length - 5} more agencies
                </p>
              )}
            </div>
          </div>
        )}

        {/* Post-Permit Requirements */}
        {commercialProcess?.post_permit_requirements &&
         commercialProcess.post_permit_requirements.length > 0 && (
          <div className="pt-3 border-t">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardList className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">After Permit Issuance</span>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 ml-7 list-disc">
              {commercialProcess.post_permit_requirements.map((req, idx) => (
                <li key={idx}>{req}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Common Violations Warning */}
        {commercialProcess?.common_violations &&
         commercialProcess.common_violations.length > 0 && (
          <Alert className="bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800">
            <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
            <AlertTitle className="text-red-800 dark:text-red-200 text-sm">
              Avoid Common Violations
            </AlertTitle>
            <AlertDescription className="text-red-700 dark:text-red-300">
              <ul className="text-sm space-y-1 ml-4 list-disc">
                {commercialProcess.common_violations.slice(0, 4).map((violation, idx) => (
                  <li key={idx}>{violation}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
